import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Criar instância do axios
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token automaticamente
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratar respostas e erros
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Verificar se é um erro 401 que não veio da rota de login
    if (error.response?.status === 401 && !error.config.url.includes('/auth/login')) {
      // Token expirado ou inválido - apenas limpar o storage
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Não redirecionamos automaticamente para permitir que o componente trate o erro
      // O redirecionamento será feito pelo AuthContext quando necessário
    }
    return Promise.reject(error);
  }
);

// Serviços de autenticação
export const authService = {
  // Registro
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    if (response.data.success && response.data.data.token) {
      localStorage.setItem('token', response.data.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.data.user));
    }
    return response.data;
  },

  // Login
  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);
    if (response.data.success && response.data.data.token) {
      localStorage.setItem('token', response.data.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.data.user));
    }
    return response.data;
  },

  // Logout
  logout: async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.log('Erro no logout:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
  },

  // Esqueci minha senha
  forgotPassword: async (email) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  // Reset de senha
  resetPassword: async (token, password, confirmPassword) => {
    const response = await api.post('/auth/reset-password', {
      token,
      password,
      confirmPassword
    });
    return response.data;
  },

  // Obter usuário atual
  me: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  // Atualizar perfil
  updateProfile: async (profileData) => {
    const response = await api.put('/auth/profile', profileData);
    if (response.data.success) {
      localStorage.setItem('user', JSON.stringify(response.data.data.user));
    }
    return response.data;
  },

  // Alterar senha
  changePassword: async (passwordData) => {
    const response = await api.put('/auth/change-password', passwordData);
    return response.data;
  },

  // Verificar se está logado
  isAuthenticated: () => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    return !!(token && user);
  },

  // Obter usuário do localStorage
  getUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  // Obter token do localStorage
  getToken: () => {
    return localStorage.getItem('token');
  }
};

// Serviços de posts
export const postService = {
  // Listar posts
  getPosts: async (params = {}) => {
    const response = await api.get('/posts', { params });
    return response.data;
  },

  // Obter post por ID
  getPostById: async (id) => {
    const response = await api.get(`/posts/id/${id}`);
    return response.data;
  },

  // Obter post por slug
  getPostBySlug: async (slug) => {
    const response = await api.get(`/posts/${slug}`);
    return response.data;
  },

  // Criar post
  createPost: async (postData) => {
    const response = await api.post('/posts', postData);
    return response.data;
  },

  // Atualizar post
  updatePost: async (id, postData) => {
    const response = await api.put(`/posts/${id}`, postData);
    return response.data;
  },

  // Auto-save
  autosavePost: async (id, postData) => {
    const response = await api.put(`/posts/${id}/autosave`, postData);
    return response.data;
  },

  // Publicar post
  publishPost: async (id) => {
    const response = await api.put(`/posts/${id}/publish`);
    return response.data;
  },

  // Despublicar post
  unpublishPost: async (id) => {
    const response = await api.put(`/posts/${id}/unpublish`);
    return response.data;
  },

  // Deletar post
  deletePost: async (id) => {
    const response = await api.delete(`/posts/${id}`);
    return response.data;
  },

  // Upload de imagens
  uploadImages: async (images) => {
    const formData = new FormData();
    if (Array.isArray(images)) {
      images.forEach(image => {
        formData.append('images', image);
      });
    } else {
      formData.append('images', images);
    }

    const response = await api.post('/posts/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }
};

// Serviços de tags
export const tagService = {
  // Listar tags
  getTags: async (params = {}) => {
    const response = await api.get('/tags', { params });
    return response.data;
  },

  // Buscar tags para autocomplete
  searchTags: async (query) => {
    const response = await api.get('/tags/search', { 
      params: { q: query, limit: 20 } 
    });
    return response.data;
  },

  // Obter tag por slug
  getTagBySlug: async (slug, params = {}) => {
    const response = await api.get(`/tags/${slug}`, { params });
    return response.data;
  },

  // Criar tag
  createTag: async (tagData) => {
    const response = await api.post('/tags', tagData);
    return response.data;
  },

  // Atualizar tag
  updateTag: async (id, tagData) => {
    const response = await api.put(`/tags/${id}`, tagData);
    return response.data;
  },

  // Deletar tag
  deleteTag: async (id) => {
    const response = await api.delete(`/tags/${id}`);
    return response.data;
  },

  // Estatísticas de tags (admin)
  getTagStats: async () => {
    const response = await api.get('/tags/admin/stats');
    return response.data;
  }
};

// Serviços de usuários (admin)
export const userService = {
  // Listar usuários
  getUsers: async (params = {}) => {
    const response = await api.get('/users', { params });
    return response.data;
  },

  // Obter usuário por ID
  getUserById: async (id) => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },

  // Atualizar status do usuário (ativar/desativar)
  updateUserStatus: async (id, isActive) => {
    const response = await api.patch(`/users/${id}/status`, { isActive });
    return response.data;
  },

  // Atualizar role do usuário
  updateUserRole: async (id, role) => {
    const response = await api.patch(`/users/${id}/role`, { role });
    return response.data;
  }
};

// Serviços de IA
export const aiService = {
  // Gerar excerpt com IA
  generateExcerpt: async (content, title) => {
    const response = await api.post('/ai/generate-excerpt', {
      content,
      title
    });
    return response.data;
  }
};

// Exportar instância do axios também para outros usos
export default api;